import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Label, Input } from 'reactstrap';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import fileDownload from 'js-file-download';
import _ from 'lodash';
import { Row, Col, Card, CardBody } from 'reactstrap';

import ReactTable from '../../custom/ReactTable';
import { notify } from '../../../utils';
import Select from '../../Select';
import { TEMP_SETTING_OPTION } from '../../../globals/interfaces';
import styled from 'styled-components';
import { FormGroup } from 'reactstrap';
import { Button } from 'reactstrap';
import { ButtonGroup } from 'reactstrap';
import useDataSummary from '../../../customHooks/useDataSummary';
import TempControlPicker from './TempControlPicker';

const Search = ({
    rackItems,
    handleAddUpdate,
    locationsMap,
    user
}) => {

    const { summary } = useDataSummary(rackItems, 't_created');

    const [filterSetting, setFilterSetting] = useState(null);
    const [filterTempSetting, setFilterTempSetting] = useState('');
    const filterDuplicates = filterSetting === 'duplicates';
    const filterTransfers = filterSetting === 'transfers';
    const filterManuallyLocated = filterSetting === 'manuallyLocated';

    const filtered = useMemo(() => {
        const filtered = [];

        for (const item of rackItems) {
            if (filterSetting === 'duplicates') {
                const { s_location } = item;
                if (_.get(locationsMap[s_location], 'numLocated', 0) >= 2) {
                    filtered.push(item);
                }
            } else if (filterSetting === 'transfers') {
                if (item.s_destination !== user.s_unit.substring(1, 4)) {
                    filtered.push(item);
                }
            } else if (filterSetting === 'manuallyLocated') {
                if (item.s_procedure === 'rackController: locateInRack' || item.s_procedure === 'controller: locateInRack') {
                    filtered.push(item);
                }
            }
        }

        return filtered;
    }, [rackItems, locationsMap, user.s_unit, filterSetting]);

    const handleSelectFilter = (filter) => {
        setFilterSetting(prev => {
            if (prev === filter) {
                return null;
            }
            return filter;
        });
    }
    
    const mapping = [
        {
            name: 'Location',
            value: 's_location'
        },
        {
            name: 'MAWB',
            value: 's_mawb',
            s_mawb: true
        },
        {
            name: 'HAWB',
            value: 's_hawb'
        },
        {
            name: 'Pieces',
            value: 'i_pieces'
        },
        {
            name: 'Dest.',
            value: 's_destination'
        },
        {
            name: 'Comat',
            value: 'b_comat',
            boolean: true
        },
        {
            name: 'USDA',
            value: 'b_usda_hold',
            boolean: true,
            labelTrue: 'HOLD',
            labelFalse: 'CLEAR'
        },
        {
            name: 'Customs',
            value: 'b_customs_hold',
            boolean: true,
            labelTrue: 'HOLD',
            labelFalse: 'CLEAR'
        },
        {
            name: 'CHOICE',
            value: 'b_hold',
            boolean: true,
            labelTrue: 'HOLD',
            labelFalse: 'CLEAR'
        },
        {
            name: 'GO',
            value: 'b_general_order',
            boolean: true
        },
        {
            name: 'Last Modified',
            value: 's_modified_by',
            email: true
        },
        {
            name: 'Storage Length',
            value: 'ic_duration_created'
        }
    ];

    const reactTableRef = useRef();

    const downloadData = () => {
        const Json2csvParser = require('json2csv').Parser;
        const raw = reactTableRef.current.getResolvedState().sortedData;
        const map = {
            s_mawb: true,
            s_hawb: true,
            s_location: true,
            i_pieces: true,
            s_special_hanlding_code: true,
            s_flight_uld: true,
            s_flight_id: true,
            s_destination: true,
            t_created: true,
            ic_duration_created: true,
        }
        if (raw.length > 0) {
            const data = raw.map(record => {
                const object = {};
                for (let key in map) {
                    const value = record._original[key]
                    object[key] = value;
                    if (key === 's_special_hanlding_code' && value !== null) {
                        const replaced = value.replace(/[,\s]/g, '');
                        if (replaced.length === 0) {
                            object[key] = '';
                        }
                    }
                }
                return object;
            });
            const jsonData = JSON.parse(JSON.stringify(data));
            const parser = new Json2csvParser({ excelStrings: true, withBOM: true });
            const csv = parser.parse(jsonData);
            fileDownload(csv, 'Rack Data.csv');
        } else {
            notify('No data to download', 'warn');
        }
    }

    const baseData = filterSetting !== null ? filtered : rackItems;

    const renderData = (filterTempSetting && filterTempSetting !== 'NONE') ?
        baseData.filter(item => item.tempSetting === filterTempSetting) :
        baseData;

    return (
        <Row>
            <Col md='12' lg='12' className='px-4'>
                <Card className='custom-opacity-card' style={{ borderRadius: '0.75rem' }}>
                    <CardBody className='custom-card-transparent py-3 px-3' style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                        <FlexContainer>
                            <h4 className={'mr-5'}>{summary}</h4>
                            <Button
                                active={filterDuplicates}
                                onClick={() => handleSelectFilter('duplicates')}
                            >
                                Duplicates
                            </Button>
                            <Button
                                active={filterTransfers}
                                onClick={() => handleSelectFilter('transfers')}
                            >
                                Transfers
                            </Button>
                            <Button
                                active={filterManuallyLocated}
                                onClick={() => handleSelectFilter('manuallyLocated')}
                            >
                                Manually Located
                            </Button>
                            <TempControlPicker 
                                filterTempSetting={filterTempSetting}
                                setFilterTempSetting={setFilterTempSetting}
                            />
                            <i
                                class="fas fa-file-download float-right text-large text-primary hover-pointer"
                                data-tip={'Download Data'}
                                onClick={() => downloadData()}
                            ></i>
                        </FlexContainer>
                        <Row className='mt-4 mb-0' style={{ fontWeight: 'bold', fontSize: '20px' }}>
                            <Col md='12' lg='12'>
                                <ReactTable
                                    data={renderData}
                                    mapping={mapping}
                                    index={true}
                                    numRows={20}
                                    customPagination={true}
                                    enableClick={true}
                                    handleClick={(item) => handleAddUpdate(false, item)}
                                    reactTableRef={reactTableRef}
                                />
                            </Col>
                        </Row>
                    </CardBody>
                </Card>
            </Col>
        </Row>
    );
}

export default withRouter(Search);

const FlexContainer = styled.div`
    display: flex;
    gap: 20px;
    align-items: center;
`;